import { MessagingChannelType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';

import { MessagingJobDriverUnreachableError } from '~hooks/useMessaging/useOpenTargetChannel';
import TargetMessagingPanel from '~pages/Dispatch/components/TargetMessaging/TargetMessagingPanel';
import MessagingAvatar, {
  MESSAGING_HEADER_AVATAR_SIZE_IN_PX,
} from '~pages/Messages/components/MessagingAvatar';

const STARTERS = [
  {
    labelKey: 'messaging.job_tab.starters.hold_at_the_pit.label',
    messageKey: 'messaging.job_tab.starters.hold_at_the_pit.message',
  },
  {
    labelKey: 'messaging.job_tab.starters.dropoff_changed.label',
    messageKey: 'messaging.job_tab.starters.dropoff_changed.message',
  },
  {
    labelKey: 'messaging.job_tab.starters.ask_for_the_ticket.label',
    messageKey: 'messaging.job_tab.starters.ask_for_the_ticket.message',
  },
];

type JobThreadPanelProps = {
  canStartConversation: boolean;
  driverAvatarColorKey: string;
  driverName: string;
  jobContext: string;
  jobId: string;
  jobLabel: string;
  readOnly?: boolean;
};

/** Takes plain values so a job the order row has not loaded can still open its thread. */
const JobThreadPanel = observer(
  ({
    canStartConversation,
    driverAvatarColorKey,
    driverName,
    jobContext,
    jobId,
    jobLabel,
    readOnly,
  }: JobThreadPanelProps) => {
    const inlineNoticeFor = useCallback(
      (error: unknown) =>
        error instanceof MessagingJobDriverUnreachableError
          ? t('messaging.job_tab.driver_unreachable', { name: driverName })
          : null,
      [driverName],
    );

    return (
      <TargetMessagingPanel
        canCreateChannel={canStartConversation}
        channelName={jobLabel}
        channelType={MessagingChannelType.JOB}
        firstMessageHint={t('messaging.job_tab.first_message_hint', { name: driverName })}
        headerAvatar={
          <MessagingAvatar
            colorKey={driverAvatarColorKey}
            name={driverName}
            size={MESSAGING_HEADER_AVATAR_SIZE_IN_PX}
          />
        }
        headerSubtitle={jobContext}
        headerTitle={driverName}
        inlineNoticeFor={inlineNoticeFor}
        noMessagesMessage={t('messaging.job_tab.no_messages', { name: driverName })}
        noPermissionMessage={t('messaging.job_tab.no_permission_to_start', {
          name: driverName,
        })}
        readOnly={readOnly}
        starters={STARTERS}
        targetId={jobId}
      />
    );
  },
);

export default JobThreadPanel;
